import type { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent'

/**
 * Inicialización del New Relic Browser agent (RUM) para la PWA.
 *
 * - La config llega por env vars `NEXT_PUBLIC_NEW_RELIC_*` (inlined en build),
 *   distintas por ambiente. Las keys del Browser agent son públicas.
 * - SSR-safe: el paquete ejecuta efectos al importarse (toca `window`), por lo
 *   que el import del módulo real es **dinámico y solo en cliente**. El import
 *   estático es `import type` (se borra en compilación, sin efecto en runtime).
 * - Arranque temprano: la carga se dispara apenas se evalúa este módulo en el
 *   cliente (importado como primer import en `_app.tsx`).
 * - No-op seguro: si falta config o `NEXT_PUBLIC_NEW_RELIC_ENABLED !== '1'`,
 *   no se instancia nada y la app sigue intacta.
 */

type NewRelicCustomAttributes = Record<string, string | number | boolean>

type NewRelicConfig = {
    licenseKey: string
    applicationID: string
    accountID: string
    agentID: string
    trustKey: string
    beacon: string
}

const readConfig = (): NewRelicConfig | null => {
    const enabled = process.env.NEXT_PUBLIC_NEW_RELIC_ENABLED === '1'
    const licenseKey = process.env.NEXT_PUBLIC_NEW_RELIC_LICENSE_KEY
    const applicationID = process.env.NEXT_PUBLIC_NEW_RELIC_APPLICATION_ID

    if (!enabled || !licenseKey || !applicationID) {
        return null
    }

    const accountID = process.env.NEXT_PUBLIC_NEW_RELIC_ACCOUNT_ID || ''
    const beacon = process.env.NEXT_PUBLIC_NEW_RELIC_BEACON || 'bam.nr-data.net'

    return {
        licenseKey,
        applicationID,
        accountID,
        agentID: process.env.NEXT_PUBLIC_NEW_RELIC_AGENT_ID || applicationID,
        trustKey: process.env.NEXT_PUBLIC_NEW_RELIC_TRUST_KEY || accountID,
        beacon
    }
}

let agent: BrowserAgent | null = null

const initNewRelic = async (): Promise<void> => {
    // Nunca en servidor: el import del paquete toca window/globals.
    if (typeof window === 'undefined') {
        return
    }
    if (agent) {
        return
    }

    const config = readConfig()
    if (!config) {
        return
    }

    try {
        // Import dinámico: solo se ejecuta en cliente (evita el crash de SSR).
        const { BrowserAgent } =
            await import('@newrelic/browser-agent/loaders/browser-agent')

        agent = new BrowserAgent({
            info: {
                beacon: config.beacon,
                errorBeacon: config.beacon,
                licenseKey: config.licenseKey,
                applicationID: config.applicationID,
                sa: 1
            },
            loader_config: {
                accountID: config.accountID,
                trustKey: config.trustKey,
                agentID: config.agentID,
                licenseKey: config.licenseKey,
                applicationID: config.applicationID
            },
            init: {
                // DT front<->edge<->Magento es trabajo cross-repo aparte.
                distributed_tracing: { enabled: false },
                // Evitar que el agente reporte sus propios beacons como AJAX.
                ajax: { deny_list: [config.beacon] },
                page_view_timing: { enabled: true },
                // Logs del browser: la feature viene on por defecto, pero sin
                // wrapLogger no se envia ningun log. Mas abajo se reenvian
                // console.error/console.warn.
                logging: { enabled: true },
                // Session Replay con muestreo conservador y PII enmascarada por
                // defecto (mask_all_inputs + mask_text_selector '*'). Requiere el
                // entitlement de Session Replay en la cuenta; sin el, el agente
                // simplemente no envia replays (no rompe nada).
                session_replay: {
                    enabled: true,
                    sampling_rate: 10,
                    error_sampling_rate: 100,
                    mask_all_inputs: true,
                    mask_text_selector: '*'
                }
            }
        })

        // Reenviar console.error y console.warn como logs del browser a New Relic
        // (feature `logging`). Captura errores y warnings de la app sin tocar los
        // JavaScriptError que ya recoge la feature jserrors.
        agent.wrapLogger(console, 'error', { level: 'ERROR' })
        agent.wrapLogger(console, 'warn', { level: 'WARN' })
    } catch (error) {
        // Nunca romper la app por fallo del agente.
        console.warn('[NewRelic] Browser agent init failed', error)
        agent = null
    }
}

// Arranque temprano solo en cliente (no-op en SSR).
if (typeof window !== 'undefined') {
    void initNewRelic()
}

export const getNewRelic = (): BrowserAgent | null => agent

export const noticeError = (
    error: Error,
    customAttributes?: NewRelicCustomAttributes
): void => {
    getNewRelic()?.noticeError(error, customAttributes)
}

export const addPageAction = (
    name: string,
    attributes?: NewRelicCustomAttributes
): void => {
    getNewRelic()?.addPageAction(name, attributes)
}

export const setCustomAttribute = (
    name: string,
    value: string | number | boolean
): void => {
    getNewRelic()?.setCustomAttribute(name, value)
}

export const setNewRelicUserId = (userId: string): void => {
    getNewRelic()?.setUserId(userId)
}
